/**
 * Salesforce Live Pulse Widget.
 *
 * Fabrizio CR 26/02/2026 — Motta opens the Salesforce Portal every
 * day. Before this widget the page was a static form with a status
 * chip: nothing screamed "the integration is *alive*". This widget
 * adds three signals visible in one glance:
 *
 *   1. **Ultima sync riuscita** — timestamp + live "X min fa" that
 *      re-renders every second (like a WhatsApp last-seen). If the
 *      last success was < 60s ago the pulse dot glows green; between
 *      60s and 1h it's amber; older / never → grey.
 *   2. **Prossima sync stimata** — computed backend-side from the
 *      average gap of the last 20 successful pushes. Only drawn when
 *      we have >= 3 successes to extrapolate from (otherwise the
 *      widget shows "In attesa di dati sufficienti").
 *   3. **Oggi**: OK / KO / in coda — three tiny KPIs with 24h freshness.
 *
 * The whole widget is a client-side render loop on the stats prop:
 * the parent (SalesforcePortalPage) fetches stats every 30s via
 * ``fetchAll()``, and this component just interpolates a smooth
 * countdown between refreshes.
 */
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Clock, Hourglass, TrendingUp, ShieldAlert, ShieldCheck, Zap, RefreshCw, ChevronDown, ChevronUp, History } from "lucide-react";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Human-friendly relative time: "42 sec fa", "3 min fa", "2 ore fa". */
const formatRelative = (deltaMs) => {
  if (deltaMs < 0) deltaMs = 0;
  if (deltaMs < MINUTE) return `${Math.floor(deltaMs / SECOND)} sec fa`;
  if (deltaMs < HOUR) return `${Math.floor(deltaMs / MINUTE)} min fa`;
  if (deltaMs < DAY) return `${Math.floor(deltaMs / HOUR)} ore fa`;
  return `${Math.floor(deltaMs / DAY)} giorni fa`;
};

/** Countdown-style: "tra 4 min 12 sec", "tra 1 ora", "in ritardo". */
const formatCountdown = (deltaMs) => {
  if (deltaMs <= 0) return "in ritardo";
  if (deltaMs < MINUTE) return `tra ${Math.ceil(deltaMs / SECOND)} sec`;
  if (deltaMs < HOUR) {
    const mins = Math.floor(deltaMs / MINUTE);
    const secs = Math.floor((deltaMs % MINUTE) / SECOND);
    return `tra ${mins} min ${secs.toString().padStart(2, "0")} sec`;
  }
  if (deltaMs < DAY) {
    const hrs = Math.floor(deltaMs / HOUR);
    const mins = Math.floor((deltaMs % HOUR) / MINUTE);
    return `tra ${hrs} ${hrs === 1 ? "ora" : "ore"} ${mins} min`;
  }
  return `tra ${Math.floor(deltaMs / DAY)} giorni`;
};

/** Pulse colour driven by freshness of the last success. */
const pulseTone = (deltaMs) => {
  if (deltaMs === null || deltaMs === undefined) {
    return { dot: "bg-gray-600", ring: "bg-gray-600/40", label: "text-gray-500" };
  }
  if (deltaMs < MINUTE) {
    return { dot: "bg-emerald-400", ring: "bg-emerald-400/40", label: "text-emerald-300" };
  }
  if (deltaMs < HOUR) {
    return { dot: "bg-amber-400", ring: "bg-amber-400/40", label: "text-amber-300" };
  }
  return { dot: "bg-gray-500", ring: "bg-gray-500/30", label: "text-gray-400" };
};

const parseIso = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Apple Wallet cert-expiry badge. Cross-signal displayed at the top
 * of the Live Pulse so the CRM guardian (Motta / Federico / Fabrizio)
 * spots a looming p12 expiry days before iOS invalidates every
 * active pass.
 *
 * Colour rules — mirrors the ``_severity_for`` thresholds on the
 * backend (services/apple_cert_monitor_service.py):
 *   * >= 61 days              → green (ok)         "OK"
 *   * 31–60 days              → sky (info)         "Rinnovo consigliato"
 *   * 16–30 days              → amber (warning)    "Rinnovo entro X gg"
 *   * 1–15 days               → red   (critical)   "URGENTE tra X gg"
 *   * <= 0                    → red pulsing        "SCADUTO"
 *
 * If ``cert`` is null (no active p12 yet on the pod, or Motta hasn't
 * uploaded her signer cert) the badge is hidden entirely — no need
 * to surface a "loading" state for a cross-signal.
 */
const AppleCertBadge = ({ cert }) => {
  if (!cert || typeof cert.days_remaining !== "number") return null;

  const days = cert.days_remaining;
  let tone;
  let label;
  let Icon = ShieldCheck;
  let pulse = false;
  if (days <= 0) {
    tone = { pill: "bg-red-500/20 text-red-200 border-red-500/50", icon: "text-red-300" };
    label = "Certificato SCADUTO";
    Icon = ShieldAlert;
    pulse = true;
  } else if (days <= 15) {
    tone = { pill: "bg-red-500/15 text-red-200 border-red-500/40", icon: "text-red-300" };
    label = `Cert Apple: URGENTE — tra ${days} ${days === 1 ? "giorno" : "giorni"}`;
    Icon = ShieldAlert;
    pulse = true;
  } else if (days <= 30) {
    tone = { pill: "bg-amber-500/15 text-amber-200 border-amber-500/40", icon: "text-amber-300" };
    label = `Cert Apple: rinnovo entro ${days} giorni`;
    Icon = ShieldAlert;
  } else if (days <= 60) {
    tone = { pill: "bg-sky-500/10 text-sky-200 border-sky-500/40", icon: "text-sky-300" };
    label = `Cert Apple: rinnovo consigliato (${days} gg)`;
  } else {
    tone = { pill: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30", icon: "text-emerald-300" };
    label = `Cert Apple OK — scade tra ${days} giorni`;
  }

  const expiryDate = cert.not_after_iso
    ? new Date(cert.not_after_iso).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${tone.pill}`}
      title={expiryDate ? `Scadenza: ${expiryDate}` : undefined}
      data-testid="sf-pulse-apple-cert-badge"
    >
      <Icon size={13} className={`${tone.icon} ${pulse ? "animate-pulse" : ""}`} />
      <span>{label}</span>
    </div>
  );
};

/**
 * 24h sparkline — 60 dots, each covering 24 minutes.
 *
 * Design intent:
 *  - Oldest bucket on the LEFT, newest on the RIGHT (reading order).
 *  - Dot size scales with sync count: empty bucket = 3px flat line
 *    (subtle track), 1 sync = normal dot, >=3 syncs = bigger dot.
 *  - Failure dots are red, success dots emerald, mixed buckets amber.
 *  - The rightmost dot pulses when the last success is <30 s old,
 *    so Motta actually SEES a sync land in real time.
 *  - Empty history renders a flat rail so the widget still tells a
 *    story ("silenzio nelle ultime 24h") instead of collapsing.
 */
const Sparkline24h = ({ buckets_ok, buckets_ko, bucketMinutes, lastOkDelta }) => {
  const ok = Array.isArray(buckets_ok) ? buckets_ok : [];
  const ko = Array.isArray(buckets_ko) ? buckets_ko : [];
  const N = Math.max(ok.length, ko.length, 60);
  const totalCount = ok.reduce((a, b) => a + b, 0) + ko.reduce((a, b) => a + b, 0);

  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="sf-pulse-sparkline">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-xs uppercase tracking-wider">
          Attività ultime 24 ore
        </span>
        <span className="text-gray-500 text-[10px] tabular-nums">
          {totalCount === 0
            ? "Nessuna sync registrata"
            : `${totalCount} sync · 1 punto = ${bucketMinutes} min`}
        </span>
      </div>
      <div className="flex items-end gap-[2px] h-8" role="img" aria-label="Sparkline 24 ore">
        {Array.from({ length: N }).map((_, i) => {
          const s = ok[i] || 0;
          const f = ko[i] || 0;
          const total = s + f;
          const isLatest = i === N - 1;
          const pulseLatest = isLatest && lastOkDelta !== null && lastOkDelta < 30 * SECOND;

          // Colour: pure success = emerald, pure fail = red, mixed = amber
          let colourClass = "bg-gray-800";
          if (total > 0) {
            if (f === 0) colourClass = "bg-emerald-400";
            else if (s === 0) colourClass = "bg-red-400";
            else colourClass = "bg-amber-400";
          }

          // Size: bigger for busier buckets
          const size = total === 0 ? "h-[3px] w-[6px]" :
                        total >= 3 ? "h-6 w-[6px]" :
                        total === 2 ? "h-4 w-[6px]" :
                                       "h-2 w-[6px]";

          const label = total === 0
            ? "0 sync"
            : `${s} riusciti${f ? `, ${f} falliti` : ""}`;

          return (
            <span
              key={i}
              title={label}
              data-testid={`sf-pulse-sparkline-bar-${i}`}
              className={`relative rounded-sm transition-all duration-500 ${size} ${colourClass} ${total === 0 ? "opacity-40" : ""}`}
            >
              {pulseLatest && (
                <span className="absolute inset-0 rounded-sm bg-emerald-400/60 animate-ping" />
              )}
            </span>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-gray-600 text-[10px]">-24h</span>
        <span className="text-gray-600 text-[10px]">-12h</span>
        <span className="text-gray-600 text-[10px]">Ora</span>
      </div>
    </div>
  );
};

export const SalesforceLivePulseWidget = ({ stats, configured, onBulkSync, bulkSyncing, bulkProgress }) => {
  // Tick every second so the "X min fa" text stays honest.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Ghost toast: a small emerald pill sliding up in the top-right
  // corner of the widget whenever a *new* successful sync lands.
  // We compare the incoming ``latest_sync_customer.completed_at``
  // against the one we saw last — if it's a fresh timestamp AND the
  // sync happened within the last 30 seconds (i.e. actually
  // "happening now" from the operator's PoV), the toast lights up.
  const [ghost, setGhost] = useState(null); // { name, memberNumber, seenAt }
  const seenTimestampRef = useRef(null);

  useEffect(() => {
    const latest = stats?.latest_sync_customer;
    if (!latest?.completed_at) return;
    // First render: remember but don't fire (avoids a phantom toast
    // on page reload for a sync that happened yesterday).
    if (seenTimestampRef.current === null) {
      seenTimestampRef.current = latest.completed_at;
      return;
    }
    if (latest.completed_at === seenTimestampRef.current) return;
    seenTimestampRef.current = latest.completed_at;

    // Only surface the toast for genuinely fresh events. Prevents
    // fireworks when a page refresh brings in a sync from 2 hours ago.
    const age = Date.now() - new Date(latest.completed_at).getTime();
    if (age > 30 * SECOND) return;

    setGhost({
      name: latest.name || "Cliente",
      memberNumber: latest.member_number,
      seenAt: Date.now(),
    });
    const dismiss = setTimeout(() => setGhost(null), 4000);
    return () => clearTimeout(dismiss);
  }, [stats?.latest_sync_customer]);

  const lastOkAt = parseIso(stats?.last_successful_sync);
  const lastKoAt = parseIso(stats?.last_failed_sync);
  const nextAt = parseIso(stats?.next_estimated_sync_at);

  const lastOkDelta = lastOkAt ? now - lastOkAt.getTime() : null;
  const lastKoDelta = lastKoAt ? now - lastKoAt.getTime() : null;
  const nextDelta = nextAt ? nextAt.getTime() - now : null;

  const tone = pulseTone(lastOkDelta);

  const todaySuccess = stats?.today_success ?? 0;
  const todayFailed = stats?.today_failed ?? 0;
  const pending = stats?.pending_customers ?? 0;
  const avgSec = stats?.avg_interval_seconds;
  const recent = Array.isArray(stats?.recent_syncs) ? stats.recent_syncs : [];
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <div
      className="wallet-card mb-6 border border-blue-600/20 bg-gradient-to-br from-[#0d1220] via-[#111827] to-[#0d1220] relative overflow-hidden"
      data-testid="sf-live-pulse-widget"
    >
      {/* Ghost toast — emerald pill sliding in on new sync landing. */}
      {ghost && (
        <div
          className="absolute top-3 right-3 z-10 pointer-events-none animate-in fade-in slide-in-from-top-3 duration-300"
          data-testid="sf-pulse-ghost-toast"
        >
          <div className="flex items-center gap-2 bg-emerald-500/95 text-emerald-950 px-3 py-2 rounded-xl shadow-lg shadow-emerald-500/30 border border-emerald-300">
            <CheckCircle2 size={16} className="shrink-0" />
            <div className="text-xs leading-tight">
              <p className="font-semibold">
                {ghost.name}
                <span className="font-normal text-emerald-900/70"> sincronizzato</span>
              </p>
              {ghost.memberNumber && (
                <p className="font-mono text-[10px] opacity-80">SF: {ghost.memberNumber}</p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            {configured && lastOkDelta !== null && lastOkDelta < HOUR && (
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full ${tone.ring} opacity-75`}
              />
            )}
            <span
              className={`relative inline-flex rounded-full h-3 w-3 ${tone.dot}`}
              data-testid="sf-live-pulse-dot"
            />
          </span>
          <h3 className="text-white font-semibold text-base tracking-tight">
            Salesforce Live Pulse
          </h3>
        </div>
        <span className={`text-xs font-medium uppercase tracking-wider ${tone.label}`}>
          {configured
            ? lastOkDelta === null
              ? "in attesa"
              : lastOkDelta < MINUTE
                ? "attivo"
                : lastOkDelta < HOUR
                  ? "recente"
                  : "in pausa"
            : "non configurato"}
        </span>
      </div>

      {/* Cross-signal: Apple Wallet cert expiry badge. Hidden if no
          cert metadata is available (e.g. .p12 not uploaded yet). */}
      {stats?.apple_cert && (
        <div className="mb-4 flex flex-wrap gap-2">
          <AppleCertBadge cert={stats.apple_cert} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Last successful sync */}
        <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-testid="sf-pulse-last-ok">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={14} className="text-emerald-400" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">
              Ultima sync riuscita
            </span>
          </div>
          {lastOkAt ? (
            <>
              <p className="text-white text-2xl font-semibold tabular-nums">
                {formatRelative(lastOkDelta)}
              </p>
              <p className="text-gray-500 text-xs mt-1" title={lastOkAt.toISOString()}>
                {lastOkAt.toLocaleString("it-IT", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-500 text-lg font-medium">Mai eseguita</p>
              <p className="text-gray-600 text-xs mt-1">
                Alla prima firma di un contratto la sync parte in automatico.
              </p>
            </>
          )}
        </div>

        {/* Next estimated sync */}
        <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-testid="sf-pulse-next">
          <div className="flex items-center gap-2 mb-2">
            <Hourglass size={14} className="text-blue-400" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">
              Prossima sync stimata
            </span>
          </div>
          {nextAt && avgSec ? (
            <>
              <p
                className={`text-2xl font-semibold tabular-nums ${
                  nextDelta > 0 ? "text-blue-300" : "text-amber-300"
                }`}
              >
                {formatCountdown(nextDelta)}
              </p>
              <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                <TrendingUp size={11} />
                media ogni {Math.round(avgSec / 60)} min
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-500 text-lg font-medium">Nessuna stima</p>
              <p className="text-gray-600 text-xs mt-1">
                Servono almeno 3 sync riuscite per calcolare il ritmo.
              </p>
            </>
          )}
        </div>

        {/* Last failed sync */}
        <div
          className={`bg-black/30 rounded-xl p-4 border ${
            lastKoDelta !== null && lastKoDelta < HOUR
              ? "border-red-500/30"
              : "border-white/5"
          }`}
          data-testid="sf-pulse-last-ko"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle
              size={14}
              className={
                lastKoDelta !== null && lastKoDelta < HOUR
                  ? "text-red-400"
                  : "text-gray-500"
              }
            />
            <span className="text-gray-400 text-xs uppercase tracking-wider">
              Ultimo errore
            </span>
          </div>
          {lastKoAt ? (
            <>
              <p
                className={`text-2xl font-semibold tabular-nums ${
                  lastKoDelta < HOUR ? "text-red-300" : "text-gray-400"
                }`}
              >
                {formatRelative(lastKoDelta)}
              </p>
              <p className="text-gray-500 text-xs mt-1" title={lastKoAt.toISOString()}>
                {lastKoAt.toLocaleString("it-IT", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </>
          ) : (
            <>
              <p className="text-emerald-300 text-lg font-medium">Nessun errore</p>
              <p className="text-gray-600 text-xs mt-1">
                Zero fallimenti registrati fin qui.
              </p>
            </>
          )}
        </div>
      </div>

      {/* 24h KPIs */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div
          className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-center"
          data-testid="sf-pulse-today-ok"
        >
          <p className="text-emerald-300 text-2xl font-bold tabular-nums">{todaySuccess}</p>
          <p className="text-gray-400 text-xs uppercase tracking-wider mt-1">Oggi OK</p>
        </div>
        <div
          className={`bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-center ${
            todayFailed > 0 ? "" : "opacity-60"
          }`}
          data-testid="sf-pulse-today-ko"
        >
          <p className="text-red-300 text-2xl font-bold tabular-nums">{todayFailed}</p>
          <p className="text-gray-400 text-xs uppercase tracking-wider mt-1">Oggi KO</p>
        </div>
        <div
          className={`bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-center ${
            pending > 0 ? "" : "opacity-60"
          }`}
          data-testid="sf-pulse-pending"
        >
          <p className="text-blue-300 text-2xl font-bold tabular-nums">{pending}</p>
          <p className="text-gray-400 text-xs uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
            <Clock size={10} /> In coda
          </p>
        </div>
      </div>

      {/* Bulk-sync CTA — only shown when SF is configured AND there
          are pending customers. Fires POST /salesforce/sync-all-customers
          via the parent's ``onBulkSync`` callback (parent controls the
          bearer + toast lifecycle). */}
      {configured && (pending > 0 || bulkProgress) && onBulkSync && (
        <div className="mt-4 bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3">
          {bulkProgress && bulkProgress.status === "running" ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 text-sm">
                  <RefreshCw size={16} className="text-blue-400 animate-spin shrink-0" />
                  <span className="text-blue-100">
                    Sync in corso —{" "}
                    <strong className="text-blue-300 tabular-nums">{bulkProgress.processed}</strong>
                    {" / "}
                    <strong className="text-blue-300 tabular-nums">{bulkProgress.total}</strong>{" "}
                    clienti
                  </span>
                </div>
                <span className="text-xs text-blue-300 font-mono">
                  {bulkProgress.total > 0
                    ? Math.floor((bulkProgress.processed / bulkProgress.total) * 100)
                    : 0}
                  %
                </span>
              </div>
              {/* Progress bar */}
              <div className="w-full h-2 bg-blue-500/10 rounded-full overflow-hidden" data-testid="sf-pulse-bulk-progress-bar">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 to-emerald-400 transition-all duration-500"
                  style={{
                    width: `${bulkProgress.total > 0
                      ? Math.min(100, (bulkProgress.processed / bulkProgress.total) * 100)
                      : 0}%`,
                  }}
                />
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span>
                  <span className="text-emerald-300 tabular-nums">{bulkProgress.created ?? 0}</span> nuovi
                </span>
                <span>
                  <span className="text-sky-300 tabular-nums">{bulkProgress.duplicates ?? 0}</span> duplicati
                </span>
                <span>
                  <span className="text-red-300 tabular-nums">{bulkProgress.failed ?? 0}</span> falliti
                </span>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Zap size={16} className="text-blue-400 shrink-0" />
                <span className="text-blue-100">
                  Ci sono <strong className="text-blue-300 tabular-nums">{pending}</strong>{" "}
                  {pending === 1 ? "cliente firmato" : "clienti firmati"} in attesa di sync verso Salesforce.
                </span>
              </div>
              <button
                type="button"
                onClick={onBulkSync}
                disabled={bulkSyncing}
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                data-testid="sf-pulse-bulk-sync-btn"
              >
                {bulkSyncing ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    Avvio…
                  </>
                ) : (
                  <>
                    <Zap size={14} />
                    Sincronizza {pending}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 24h sparkline — 60 dots × 24 min covering the last day.
          Newest dot on the right; freshest dot pulses when it lit up
          within the last 30 sec so Motta actually SEES a sync land. */}
      <Sparkline24h
        buckets_ok={stats?.sparkline_buckets_ok}
        buckets_ko={stats?.sparkline_buckets_ko}
        bucketMinutes={stats?.sparkline_bucket_minutes || 24}
        lastOkDelta={lastOkDelta}
      />

      {/* Expandable mini-log — last 5 successful syncs. Collapsed by
          default to keep the widget short; a click reveals a compact
          list with customer name, SF record id and relative time. */}
      {recent.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="sf-pulse-recent-syncs">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-gray-200 transition-colors"
            data-testid="sf-pulse-recent-toggle"
            aria-expanded={historyOpen}
          >
            <span className="uppercase tracking-wider flex items-center gap-2">
              <History size={12} /> Ultimi {recent.length} clienti sincronizzati
            </span>
            {historyOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {historyOpen && (
            <ul className="mt-3 space-y-1.5" data-testid="sf-pulse-recent-list">
              {recent.map((r, i) => {
                const at = r.completed_at ? new Date(r.completed_at) : null;
                const rel = at ? formatRelative(now - at.getTime()) : "—";
                return (
                  <li
                    key={`${r.customer_id}-${i}`}
                    className="flex items-center justify-between gap-3 text-xs bg-black/20 rounded-lg px-3 py-2 border border-white/5"
                    data-testid={`sf-pulse-recent-item-${i}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                      <span className="text-gray-100 truncate">{r.name}</span>
                      {r.member_number && (
                        <span className="text-gray-500 font-mono text-[10px] hidden sm:inline">
                          · {r.member_number}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-500 tabular-nums shrink-0" title={at?.toISOString()}>
                      {rel}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesforceLivePulseWidget;
